export type HireWizardUploadedFile = {
  id: string;
  name: string;
  size: number;
  type: string;
  text: string;
};

export type HireWizardWidgetColors = {
  headerColor: string;
  widgetButtonColor: string;
  launcherBackgroundColor?: string;
  textColor?: string;
};

export type HireWizardTrainingDraft = {
  primaryWebsiteUrl: string;
  additionalWebsiteUrls: string[];
  scrapedPageUrls: string[];
  textSnippets: string[];
  qaPairs: Array<{ question: string; answer: string }>;
  uploadedFiles: HireWizardUploadedFile[];
  companyProfileGenerated: boolean;
  companyProfileText: string;
  faqsGenerated: boolean;
  widgetColors: HireWizardWidgetColors;
};

export type HireWizardToolCredentials = Record<string, Record<string, string>>;

const TRAINING_KEY = "egpt-hire-wizard-training";
const TOOLS_KEY = "egpt-hire-wizard-tool-credentials";

export const DEFAULT_HIRE_WIDGET_COLORS: HireWizardWidgetColors = {
  headerColor: "#2563eb",
  widgetButtonColor: "#2563eb",
  launcherBackgroundColor: "#2563eb",
  textColor: "#ffffff",
};

export function defaultHireWizardTrainingDraft(): HireWizardTrainingDraft {
  return {
    primaryWebsiteUrl: "",
    additionalWebsiteUrls: [],
    scrapedPageUrls: [],
    textSnippets: [],
    qaPairs: [],
    uploadedFiles: [],
    companyProfileGenerated: false,
    companyProfileText: "",
    faqsGenerated: false,
    widgetColors: { ...DEFAULT_HIRE_WIDGET_COLORS },
  };
}

export function readHireWizardTrainingDraft(): HireWizardTrainingDraft {
  if (typeof window === "undefined") return defaultHireWizardTrainingDraft();
  try {
    const raw = sessionStorage.getItem(TRAINING_KEY);
    if (!raw) return defaultHireWizardTrainingDraft();
    const parsed = JSON.parse(raw) as Partial<HireWizardTrainingDraft>;
    return {
      ...defaultHireWizardTrainingDraft(),
      ...parsed,
      primaryWebsiteUrl: parsed.primaryWebsiteUrl ?? "",
      additionalWebsiteUrls: parsed.additionalWebsiteUrls ?? [],
      scrapedPageUrls: parsed.scrapedPageUrls ?? [],
      textSnippets: parsed.textSnippets ?? [],
      qaPairs: parsed.qaPairs ?? [],
      uploadedFiles: parsed.uploadedFiles ?? [],
      companyProfileText: parsed.companyProfileText ?? "",
      widgetColors: {
        ...DEFAULT_HIRE_WIDGET_COLORS,
        ...(parsed.widgetColors ?? {}),
      },
    };
  } catch {
    return defaultHireWizardTrainingDraft();
  }
}

export function writeHireWizardTrainingDraft(draft: HireWizardTrainingDraft): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(TRAINING_KEY, JSON.stringify(draft));
  } catch {
    /* ignore */
  }
}

export function readHireWizardToolCredentials(): HireWizardToolCredentials {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(TOOLS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as HireWizardToolCredentials & { emrSkipped?: boolean };
    const { emrSkipped: _ignored, ...credentials } = parsed;
    return credentials;
  } catch {
    return {};
  }
}

export function readHireWizardEmrCredentialsSkipped(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const raw = sessionStorage.getItem(TOOLS_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { emrSkipped?: boolean };
    return parsed.emrSkipped === true;
  } catch {
    return false;
  }
}

export function writeHireWizardToolCredentials(
  credentials: HireWizardToolCredentials,
  options?: { emrSkipped?: boolean },
): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(
      TOOLS_KEY,
      JSON.stringify({ ...credentials, emrSkipped: options?.emrSkipped === true }),
    );
  } catch {
    /* ignore */
  }
}
